import { useCallback, useMemo, useState } from 'react'
import { type FieldError, useWatch } from 'react-hook-form'

import EditIcon from '@mui/icons-material/Edit'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import type { ShopeeLogisticsProviderInput } from '~/common/schemas/logistics-shopee'
import { useChannelLogisticsQuery } from '~/products/hooks/useChannelLogisticsQuery'
import { type ProductFormSchema, useProductFormContext } from '~/products/hooks/useProductForm'
import { getProviderLimits } from '~/products/utils/getProviderLimits'
import { LogisticsProviderDialog } from '../logistics/logistics-provider-dialog'
import { LogisticsTable } from '../logistics/logistics-table'
import { ShopeeLogisticsProviders } from './shopee-attributes'

function isSellerOwnFleet(value: ShopeeLogisticsProviderInput[]): boolean {
    return value.length === 1 && value[0]?.fee_type === 'CUSTOM_PRICE'
}

type ChannelProduct = ProductFormSchema['channel_products'][0]

type ShopeeEnabledLogisticsCellProps = {
    shopId: number
    channelId: number
}

function ShopeeEnabledLogisticsCell({ shopId, channelId }: ShopeeEnabledLogisticsCellProps) {
    const { data, status } = useChannelLogisticsQuery({ channel: 'shopee', shopId })

    const { formState, control, register } = useProductFormContext()
    const channelAttributeValuesErrors = formState.errors.channel_attribute_values?.[channelId]
        ?.product?.logistics?.value as Record<string, FieldError> | undefined

    const shopSpecificErrors = channelAttributeValuesErrors?.[`shop-${shopId}`]

    const { name } = register(
        `channel_attribute_values.${channelId}.product.logistics.value.shop-${shopId}`,
        {
            validate: (logisticsAttribute, form) => {
                if (!logisticsAttribute) {
                    return gettext('No logistics providers selected. You must select at least one.')
                }

                if (logisticsAttribute.channel !== 'shopee') {
                    return gettext('Invalid logistics provider data.')
                }

                const value = logisticsAttribute.value
                if (value.every((lp) => !lp.enabled)) {
                    return gettext('No logistics providers selected. You must select at least one.')
                }

                if (isSellerOwnFleet(value)) {
                    return true
                }

                const productWeight = Number(form.package_weight)
                const limits = getProviderLimits(value)
                if (productWeight > limits.weight.maxValue) {
                    return gettext(
                        'The product weight exceeds the maximum weight allowed by the logistics provider.'
                    )
                }

                return true
            },
        }
    )

    const existingLogisticsProviders = useWatch({
        control,
        name: `channel_attribute_values.${channelId}.product.logistics.value.shop-${shopId}`,
        defaultValue: {
            channel: 'shopee',
            value: [],
        },
    })

    if (status === 'loading') {
        return <Skeleton width={200} />
    }

    if (status === 'error') {
        return (
            <Typography color="error" variant="subtitle2">
                {gettext('Failed to load logistics providers')}
            </Typography>
        )
    }

    if (existingLogisticsProviders.channel !== 'shopee') {
        return null
    }

    const existingLogisticsProviderSet = new Set(
        existingLogisticsProviders.value.filter((lp) => lp.enabled).map((lp) => lp.logistic_id)
    )

    const providersToDisplay = data.filter((lp) => {
        return existingLogisticsProviderSet.has(lp.logistic_id)
    })

    return (
        <div data-path={name}>
            {providersToDisplay.length > 0 && (
                <Typography gutterBottom>
                    {providersToDisplay.map((lp) => lp.logistic_name).join(', ')}
                </Typography>
            )}
            {providersToDisplay.length === 0 && !shopSpecificErrors && (
                <Typography variant="body2">
                    {gettext('No logistics providers selected. You must select at least one.')}
                </Typography>
            )}
            {shopSpecificErrors && (
                <Typography color="error" variant="subtitle2">
                    {shopSpecificErrors.message}
                </Typography>
            )}
        </div>
    )
}

type Props = {
    name: `channel_attribute_values.${string}.product.${string}.value`
    shopeeProducts: ChannelProduct[]
    shopIds: number[]
    countryCode: string | null
}

export default function ShopeeLogistics({ shopeeProducts, countryCode }: Props) {
    const [editingProvidersForChannelProductId, setEditingProvidersForChannelProductId] = useState<
        string | number
    >(-1)

    const editingProvidersForChannelProduct = useMemo(() => {
        return shopeeProducts.find((cp) => cp.id === editingProvidersForChannelProductId) ?? null
    }, [editingProvidersForChannelProductId, shopeeProducts])

    const [channelProductProvidersOpen, setChannelProductProvidersOpen] = useState(false)

    const handleEditChannelProductProviders = useCallback((channelProductId: string | number) => {
        setChannelProductProvidersOpen(true)
        setEditingProvidersForChannelProductId(channelProductId)
    }, [])

    return (
        <>
            <LogisticsTable
                channelProducts={shopeeProducts}
                logisticsCol={{
                    cell: ({ row }) => (
                        <ShopeeEnabledLogisticsCell
                            shopId={row.original.shop.id}
                            channelId={row.original.shop.channel.id}
                        />
                    ),
                }}
                actionsCol={{
                    cell: (props) => (
                        <Tooltip title={gettext('Edit logistics providers')}>
                            <IconButton
                                color="secondary"
                                onClick={() =>
                                    handleEditChannelProductProviders(props.row.original.id)
                                }
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    ),
                }}
            />
            {editingProvidersForChannelProduct && (
                <LogisticsProviderDialog
                    open={channelProductProvidersOpen}
                    title={editingProvidersForChannelProduct.shop.shop_name}
                    onClose={() => setChannelProductProvidersOpen(false)}
                    onExited={() => setEditingProvidersForChannelProductId(-1)}
                >
                    <ShopeeLogisticsProviders
                        shopId={editingProvidersForChannelProduct.shop.id}
                        countryCode={countryCode}
                    />
                </LogisticsProviderDialog>
            )}
        </>
    )
}
